import Big from 'big.js';
import type { Security } from '../types/Security';
import { ProductTypeEnum } from '../types/types';

const THIRTY_SECONDS_PATTERN = '[0-2]\\d|3[01]';
const THIRTY_SECONDS_CAPTURE = `(${THIRTY_SECONDS_PATTERN})`;
const SUFFIX_CHAR_PATTERN = '[01234567+]';

const COMPLETE_TREASURY_PRICE_PATTERN = new RegExp(`^(-?\\d+)-${THIRTY_SECONDS_CAPTURE}(${SUFFIX_CHAR_PATTERN})$`);
const SHORTHAND_TREASURY_PRICE_PATTERN = new RegExp(`^(-?\\d+)-${THIRTY_SECONDS_CAPTURE}$`);

function formatSuffixChar(eighthIndex: number): string {
  return eighthIndex === 4 ? '+' : String(eighthIndex);
}

function parseSuffixChar(suffixChar: string): number | undefined {
  if (suffixChar === '+' || suffixChar === '4') {
    return 4;
  }

  const digit = Number(suffixChar);
  if (Number.isInteger(digit) && digit >= 0 && digit <= 7) {
    return digit;
  }

  return undefined;
}

/** Formats US Treasury prices in 32nds with cash-market eighth-of-a-32nd suffixes (0–7, + for half). */
export function formatUSTreasuryPrice(price: Big): string {
  return formatUSTreasuryPriceWithRounding(price).text;
}

export function formatUSTreasuryPriceWithRounding(price: Big): { text: string; isRounded: boolean } {
  const isNegative = price.lt(0);
  const ticks256 = price.abs().times(256).round(0, Big.roundHalfUp);
  const wholePoints = ticks256.div(256).round(0, Big.roundDown);
  const fractional256 = ticks256.minus(wholePoints.times(256));
  const thirtySeconds = fractional256.div(8).round(0, Big.roundDown);
  const thirtySecondsText = thirtySeconds.toFixed().padStart(2, '0');
  const suffixChar = formatSuffixChar(fractional256.mod(8).toNumber());
  const formatted = `${wholePoints.toFixed()}-${thirtySecondsText}${suffixChar}`;

  return {
    text: isNegative ? `-${formatted}` : formatted,
    isRounded: !price.abs().times(256).eq(ticks256),
  };
}

export function roundUSTreasuryPriceToTick(decimalPrice: Big): Big {
  return decimalPrice.times(256).round(0, Big.roundHalfUp).div(256);
}

export function isCompleteUSTreasuryPrice(value: string): boolean {
  if (SHORTHAND_TREASURY_PRICE_PATTERN.test(value)) {
    return true;
  }

  if (!COMPLETE_TREASURY_PRICE_PATTERN.test(value)) {
    return false;
  }

  const suffixChar = value.at(-1);
  if (suffixChar == null) {
    return false;
  }

  return parseSuffixChar(suffixChar) != null;
}

export function parseUSTreasuryPrice(value: string): Big {
  if (!isCompleteUSTreasuryPrice(value)) {
    throw new Error(`Invalid US Treasury 32nds price: ${value}`);
  }

  const { wholePointsText, thirtySeconds, suffixChar } = parseTreasuryPriceParts(value);
  const eighthIndex = parseSuffixChar(suffixChar);
  if (eighthIndex == null) {
    throw new Error(`Invalid US Treasury 32nds price: ${value}`);
  }

  const fractional256 = thirtySeconds * 8 + eighthIndex;
  const isNegative = wholePointsText.startsWith('-');
  const absoluteWholePoints = isNegative ? wholePointsText.slice(1) : wholePointsText;
  const decimalPrice = Big(absoluteWholePoints).plus(Big(fractional256).div(256));

  return isNegative ? decimalPrice.times(-1) : decimalPrice;
}

/**
 * Normalizes existing Treasury 32nds notation into its standard text representation.
 * Pads shorthand with a zero suffix and uses `+` for half a 32nd; preserves the sign.
 * Incomplete or invalid notation (including decimal strings) is returned unchanged,
 * so this can safely run on input blur. To format a decimal price, use `formatUSTreasuryPrice`.
 * @example canonicalizeUSTreasuryPrice('105-13') // '105-130'
 * @example canonicalizeUSTreasuryPrice('105-134') // '105-13+'
 */
export function canonicalizeUSTreasuryPrice(value: string): string {
  if (!isCompleteUSTreasuryPrice(value)) {
    return value;
  }

  return formatUSTreasuryPrice(parseUSTreasuryPrice(value));
}

function parseTreasuryPriceParts(value: string): {
  wholePointsText: string;
  thirtySeconds: number;
  suffixChar: string;
} {
  const shorthandMatch = SHORTHAND_TREASURY_PRICE_PATTERN.exec(value);
  if (shorthandMatch != null) {
    return {
      wholePointsText: shorthandMatch[1],
      thirtySeconds: Number(shorthandMatch[2]),
      suffixChar: '0',
    };
  }

  const match = COMPLETE_TREASURY_PRICE_PATTERN.exec(value);
  if (match == null) {
    throw new Error(`Invalid US Treasury 32nds price: ${value}`);
  }

  return {
    wholePointsText: match[1],
    thirtySeconds: Number(match[2]),
    suffixChar: match[3],
  };
}

export function isUSTreasurySecurity(security: Pick<Security, 'ProductType'> | undefined): boolean {
  return security?.ProductType === ProductTypeEnum.GovernmentBond;
}
